/**
 * Shared React hooks for the ops dashboard.
 */

import { useState, useEffect, useCallback, useRef } from "react";

/**
 * Generic async data fetcher with loading/error state.
 * Re-fetches whenever deps change (shallow equality via JSON).
 */
export function useAsync<T>(
  fetcher: () => Promise<T>,
  deps: unknown[] = []
): { data: T | null; loading: boolean; error: string | null; reload: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const counter = useRef(0);

  const load = useCallback(() => {
    const id = ++counter.current;
    setLoading(true);
    setError(null);
    fetcher()
      .then((d) => { if (id === counter.current) { setData(d); setLoading(false); } })
      .catch((e) => { if (id === counter.current) { setError(e?.message ?? "Error"); setLoading(false); } });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => { load(); }, [load]);

  return { data, loading, error, reload: load };
}

/** Debounced value hook — delays state update by `ms` after last change. */
export function useDebounce<T>(value: T, ms = 350): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

/** Auto-refresh hook — calls reload() every `intervalMs`. */
export function useAutoRefresh(reload: () => void, intervalMs: number): void {
  useEffect(() => {
    const id = setInterval(reload, intervalMs);
    return () => clearInterval(id);
  }, [reload, intervalMs]);
}
